/**
 * Admin → Notifications — what the app sends, when, and to whom.
 *
 * The answer to "why did I get this email?", which until now required reading
 * source code. Deliberately READ-ONLY: it explains delivery, it does not
 * configure it. Per-user preferences were dropped in #757 in favour of paired
 * delivery (always in-app + email), and a second control surface would make
 * that worse while the first one is still lying around unread (#1011).
 *
 * The catalogue comes from the backend, where a test walks every `event=`
 * literal in the codebase and fails if this page has drifted from reality.
 */

import {
  Badge,
  Caption1,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { useNotificationCatalog, type NotificationCatalogEntry } from '../hooks/useAdminUsers'
import { ErrorCard } from '../components/ErrorCard'
import { apiErrorMessage } from '../lib/apiError'

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  lead: { color: tokens.colorNeutralForeground2, maxWidth: '70ch' },
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  groupTitle: { fontWeight: tokens.fontWeightSemibold },
  groupNote: { color: tokens.colorNeutralForeground3, maxWidth: '70ch' },
  key: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  note: { color: tokens.colorNeutralForeground3, display: 'block', marginTop: '2px' },
  dead: { opacity: 0.65 },
  // The one column people actually go looking for — worth finding at a glance.
  optOut: { color: tokens.colorPaletteGreenForeground1, fontWeight: tokens.fontWeightSemibold },
  tableWrap: { overflowX: 'auto' },
})

const GROUPS: Array<{ key: string; title: string; note: string }> = [
  {
    key: 'user_actions',
    title: 'Someone did something',
    note: 'Sent immediately, as a direct result of an action in the app.',
  },
  {
    key: 'demand_closed',
    title: 'A demand was closed',
    note: 'One message per closure type, all to the people who had applied.',
  },
  {
    key: 'scheduled',
    title: 'On a schedule',
    note: 'Sent by a background check, with nobody pressing anything.',
  },
  {
    key: 'declared_but_never_sent',
    title: 'Declared, but never sent',
    note:
      'These event names exist in the code and in the focus-area preference table, ' +
      'but nothing dispatches them. Listed so this page tells the whole truth — ' +
      'if you were expecting one of these, it has never arrived and never will ' +
      'until somebody builds it.',
  },
]

function Group({ title, note, rows }: { title: string; note: string; rows: NotificationCatalogEntry[] }) {
  const styles = useStyles()
  if (!rows?.length) return null
  const dead = rows.every((r) => !r.emitted)

  return (
    <section className={styles.group} data-testid={`catalog-group-${title}`}>
      <Title3 className={styles.groupTitle}>{title}</Title3>
      <Caption1 className={styles.groupNote}>{note}</Caption1>
      <div className={styles.tableWrap}>
        <Table size="small" aria-label={title}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '20%' }}>Event</TableHeaderCell>
              <TableHeaderCell style={{ width: '28%' }}>What triggers it</TableHeaderCell>
              <TableHeaderCell style={{ width: '26%' }}>Who receives it</TableHeaderCell>
              <TableHeaderCell style={{ width: '26%' }}>Can it be turned off?</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} className={dead ? styles.dead : undefined}>
                <TableCell>
                  <span className={styles.key}>{r.key}</span>
                  {!r.emitted && (
                    <>
                      {' '}
                      <Badge appearance="tint" color="warning" size="small">
                        never sent
                      </Badge>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {r.emitted ? r.trigger : '—'}
                  {r.note && <Caption1 className={styles.note}>{r.note}</Caption1>}
                </TableCell>
                <TableCell>{r.emitted ? r.audience : '—'}</TableCell>
                <TableCell>
                  {r.emitted ? (
                    <Caption1 className={r.opt_out?.startsWith('Yes') ? styles.optOut : undefined}>
                      {r.opt_out}
                    </Caption1>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}

export function AdminNotificationCatalog() {
  const styles = useStyles()
  const { data, isLoading, error } = useNotificationCatalog()

  if (isLoading) return <Spinner label="Loading…" />
  if (error) return <ErrorCard message={apiErrorMessage(error, 'Could not load the catalogue')} />

  const groups = data?.groups ?? {}

  return (
    <div className={styles.page}>
      <div>
        <Title3>Notifications</Title3>
        <Text block className={styles.lead}>
          Every message the platform sends, what causes it, and whether you can stop
          it. Delivery is paired — anything listed here arrives both as an in-app
          notification and as an email.
        </Text>
      </div>

      {GROUPS.map((g) => (
        <Group key={g.key} title={g.title} note={g.note} rows={groups[g.key] ?? []} />
      ))}
    </div>
  )
}
